"use client"

import { useEffect, useRef, useState } from "react"
import { loadTurnstileScript } from "@/lib/turnstile-script"

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

// Official interactive test key. Production must supply a real site key.
const DEVELOPMENT_TEST_SITE_KEY = "3x00000000000000000000FF"

type TurnstileWidgetProps = {
  onTokenChange: (token: string | null) => void
  resetKey: number
  action: "user_login" | "admin_login" | "user_register"
}

export function TurnstileWidget({ onTokenChange, resetKey, action }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const callbackRef = useRef(onTokenChange)
  const [attempt, setAttempt] = useState(0)
  const [status, setStatus] = useState<"loading" | "slow" | "ready" | "error">("loading")
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ||
    (process.env.NODE_ENV === "production" ? "" : DEVELOPMENT_TEST_SITE_KEY)

  useEffect(() => { callbackRef.current = onTokenChange }, [onTokenChange])

  useEffect(() => {
    if (!siteKey) return
    let active = true
    let failed = false
    let widgetId: string | null = null
    callbackRef.current(null)
    setStatus("loading")
    const timer = setTimeout(() => {
      if (active && widgetId === null) setStatus("slow")
    }, 6000)
    const fail = () => {
      if (!active) return
      failed = true
      clearTimeout(timer)
      callbackRef.current(null)
      setStatus("error")
    }
    void loadTurnstileScript(attempt > 0).then(() => {
      if (!active || !containerRef.current || !window.turnstile) return
      clearTimeout(timer)
      widgetId = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action,
        theme: "auto",
        size: "normal",
        callback: (token: string) => { if (active && !failed) callbackRef.current(token) },
        "expired-callback": () => { if (active) callbackRef.current(null) },
        "error-callback": fail,
        "timeout-callback": fail,
      })
      if (!failed) setStatus("ready")
    }).catch(fail)
    return () => {
      active = false
      clearTimeout(timer)
      callbackRef.current(null)
      if (widgetId !== null && window.turnstile) window.turnstile.remove(widgetId)
    }
  }, [action, attempt, resetKey, siteKey])

  if (!siteKey) {
    return (
      <p role="alert" style={{ color: "var(--berry-500)", fontSize: 13 }}>
        人机验证尚未配置，请联系管理员。
      </p>
    )
  }

  return (
    <div className="login-turnstile">
      <div>
        <div ref={containerRef} className="login-turnstile__mount" />
        {status !== "ready" && (
          <p role="status" style={{ color: "var(--berry-500)", fontSize: 13 }}>
            {status === "error" ? "人机验证加载失败，请重试。" : status === "slow" ? "人机验证加载较慢，请稍候或重试。" : "正在加载人机验证…"}
            {status !== "loading" && <button type="button" className="ml-2 underline" onClick={() => setAttempt(value => value + 1)}>重试</button>}
          </p>
        )}
      </div>
    </div>
  )
}
